const quantityFormat = new Intl.NumberFormat('tr-TR', { maximumFractionDigits: 3 });
const quantityFormat6 = new Intl.NumberFormat('tr-TR', { maximumFractionDigits: 6 });

export function formatQuantity(value: number | null | undefined, unit?: string | null): string {
  if (value == null || Number.isNaN(value)) return '—';
  const text = quantityFormat.format(value);
  const trimmed = unit?.trim();
  return trimmed ? `${text} ${trimmed}` : text;
}

export function formatMoney(value: number | null | undefined, fractionDigits = 2): string {
  if (value == null || Number.isNaN(value)) return '—';
  return new Intl.NumberFormat('tr-TR', {
    style: 'currency',
    currency: 'TRY',
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(value);
}

/** Takvim gününü saat dilimine kaydırmadan dd.MM.yyyy yazar. */
export function formatProductionDate(value?: string | null): string {
  if (!value) return '—';
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!match) return '—';
  return `${match[3]}.${match[2]}.${match[1]}`;
}

export function shortCreatedBy(value?: string | null): string {
  const trimmed = value?.trim();
  if (!trimmed) return '—';
  const at = trimmed.indexOf('@');
  return at > 0 ? trimmed.slice(0, at) : trimmed;
}

export function formatWarehouses(input?: string | null, output?: string | null): string {
  const left = input?.trim() || '';
  const right = output?.trim() || '';
  if (!left && !right) return '—';
  if (!left || !right || left === right) return left || right;
  return `${left} → ${right}`;
}

/** En fazla 6 ondalık, sondaki sıfırlar atılır, tr-TR. */
export function formatQty(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return '—';
  return quantityFormat6.format(value);
}

/** P10 Round6 ile aynı: 6 hane, 0'dan uzak. */
export function round6(value: number): number {
  if (!Number.isFinite(value)) return 0;
  const sign = value < 0 ? -1 : 1;
  return (sign * Math.round(Math.abs(value) * 1_000_000)) / 1_000_000;
}

/** Karşılaştırma: trim + tr-TR küçük harf. */
export function sameUnitName(left?: string | null, right?: string | null): boolean {
  const a = (left ?? '').trim().toLocaleLowerCase('tr-TR');
  const b = (right ?? '').trim().toLocaleLowerCase('tr-TR');
  return a.length > 0 && a === b;
}

/** Gösterim miktarı: ana / çarpan. Çarpan yoksa ana miktar. */
export function toDisplayQty(base: number, factor?: number | null): number {
  const scale = factor != null && factor !== 0 ? factor : 1;
  return round6(base / scale);
}

/** Ana miktar: gösterilen × çarpan. */
export function toBaseQty(display: number, factor?: number | null): number {
  const scale = factor != null && factor !== 0 ? factor : 1;
  return round6(display * scale);
}

/** Virgül veya nokta kabul eder, 6 haneye yuvarlar. Boşsa NaN. */
export function parseFactor(value: string): number {
  const cleaned = value.trim().replace(/\s/g, '').replace(',', '.');
  if (!cleaned) return Number.NaN;
  const parsed = Number(cleaned);
  if (!Number.isFinite(parsed)) return Number.NaN;
  return round6(parsed);
}

/** Kayıt gövdesi: ana birimde EntryUnit null, alternatifte görünen miktar. */
export function entryLineQuantities(row: {
  unit: string;
  entryUnit?: string | null;
  entryFactor?: number | null;
  plannedQuantity: number;
  actualQuantity: number;
}): { EntryUnit: string | null; PlannedQuantity: number; ActualQuantity: number } {
  const alt = row.entryUnit && !sameUnitName(row.entryUnit, row.unit) ? row.entryUnit.trim() : null;
  if (!alt) {
    return {
      EntryUnit: null,
      PlannedQuantity: row.plannedQuantity,
      ActualQuantity: row.actualQuantity,
    };
  }
  return {
    EntryUnit: alt,
    PlannedQuantity: toDisplayQty(row.plannedQuantity, row.entryFactor),
    ActualQuantity: toDisplayQty(row.actualQuantity, row.entryFactor),
  };
}

export function isFutureCalendarDate(value: Date | null): boolean {
  if (!value || Number.isNaN(value.getTime())) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const day = new Date(value);
  day.setHours(0, 0, 0, 0);
  return day.getTime() > today.getTime();
}

export function formatDateTime(value?: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat('tr-TR', { dateStyle: 'short', timeStyle: 'short' }).format(date);
}

export function formatPercent(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return '—';
  return `${new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value)}%`;
}

export function toCalendarDate(value: Date | null): string | undefined {
  if (!value || Number.isNaN(value.getTime())) return undefined;
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}
